// JOBE-WSC-B2: SPA renderer for the operator-approval flow.
//
// Wire-shape source of truth: specs/completed/JOBE-APPROVAL-events.md
// (PR #507). This component is the generic, host-agnostic prompt — the
// chat surface (Chat.jsx) hosts it now; the dispatcher console, the
// Overview, and ADR-0021 M-5's memory-forget surface will reuse it.
//
// Generic by design: there are no chat-specific assumptions baked in.
// The host supplies a `payloadRenderer` that knows how to render the
// `kind`-specific payload (e.g. for `chat.tool_invoke`, render the
// tool name + args; for `memory.forget`, render the target memory).
// Unknown kinds get a generic JSON fallback.
//
// State model:
//   - Local state per request_id: `pending | approved | rejected | expired`
//   - Local state is for snappiness only. The source of truth is the
//     `approval.resolved` event arriving on the same thread (the SPA's
//     existing WS subscription); when that lands the host should
//     re-render this component with `resolved` set so the local state
//     is reconciled against the operator-authoritative event.
//   - Submit POSTs to `/api/approvals/{request_id}/resolve`. On 200
//     update local state and call `onResolved`. On 410 mark as expired.
//     On 4xx other surface error inline.
//   - Countdown is computed client-side from `expires_at` so the UI
//     can degrade visually, but the "expired" terminal state must come
//     from the operator-minted `approval.resolved` event (per
//     JOBE-APPROVAL-events §5: "MUST reconcile against the eventual
//     server-minted event").

import { useEffect, useMemo, useState } from 'react';

import { resolveApproval } from './lib/store.js';

// isApprovalRequested recognises the `approval.requested` envelope
// event type. Only one spelling is canonical here — unlike tool.call /
// tool_call.requested (where ADR-0022 and the operator's actual emitter
// disagree), JOBE-APPROVAL-events §2 pins exactly `approval.requested`
// and `approval.resolved`.
export function isApprovalRequested(eventType) {
  return eventType === 'approval.requested';
}

// isApprovalResolved recognises the `approval.resolved` envelope event
// type.
export function isApprovalResolved(eventType) {
  return eventType === 'approval.resolved';
}

// approvalRequestID extracts the join key from either an
// `approval.requested` or `approval.resolved` envelope body. Returns
// the empty string when absent (defensive — a malformed event must not
// pair with anything).
export function approvalRequestID(ev) {
  if (!ev) return '';
  const body = ev.body || ev.payload || {};
  return body.request_id ? String(body.request_id) : '';
}

// findResolvedFor returns the matching `approval.resolved` event for
// the given request_id, or undefined if not yet present. Mirrors
// ToolCallRow's `findResultFor` (B-1) so chat-row pairing stays
// consistent across the two flows.
export function findResolvedFor(events, requestID) {
  if (!requestID || !Array.isArray(events)) return undefined;
  for (const ev of events) {
    const t = ev?.event_type || ev?.type || '';
    if (!isApprovalResolved(t)) continue;
    if (approvalRequestID(ev) === requestID) return ev;
  }
  return undefined;
}

// fmtCountdown formats a millisecond duration as a compact
// "Xm Ys" / "Ys" string. Negative inputs render as "expired" so the
// countdown UI degrades visibly before the operator-minted event
// arrives.
function fmtCountdown(ms) {
  if (typeof ms !== 'number' || Number.isNaN(ms)) return '';
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// fmtTime renders a wall-clock timestamp as a short, locale-aware
// label. Mirrors Chat.jsx's local helper so the two surfaces stay
// visually consistent without coupling.
function fmtTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// genericPayloadRenderer is the fallback for unknown `kind` values.
// Renders the payload as a pretty-printed JSON block so the operator
// can still see what's being asked even if the UI hasn't learned the
// new kind yet. Per JOBE-APPROVAL-events §2.1: unknown kinds MUST be
// rendered with a generic fallback, not rejected.
function genericPayloadRenderer(payload) {
  if (payload === null || payload === undefined) return null;
  let pretty;
  try {
    pretty = JSON.stringify(payload, null, 2);
  } catch {
    pretty = String(payload);
  }
  return (
    <pre className="approval-payload-generic mono">{pretty}</pre>
  );
}

// decisionFromResolved returns the terminal local-state for a resolved
// event body. Maps `expired` discriminator to the expired terminal
// state regardless of the `decision` enum (defence-in-depth: the spec
// requires both fields to agree, but consumers branch on `expired` per
// §2.2's discriminator design).
function decisionFromResolved(body) {
  if (!body) return null;
  if (body.expired === true || body.decision === 'expired') return 'expired';
  if (body.decision === 'approved') return 'approved';
  if (body.decision === 'rejected') return 'rejected';
  return null;
}

// ApprovalPrompt renders one approval-requested event as a card with
// approve/reject affordances, and (when the matching resolved event
// has arrived) collapses to a compact trace row.
//
// Props:
//   request:          the `approval.requested` envelope. Body fields per
//                     JOBE-APPROVAL-events §2.1 (request_id, kind,
//                     subject_principal, summary, payload, created_at,
//                     expires_at, auto_action_on_expiry).
//   resolved:         the matching `approval.resolved` envelope, or
//                     undefined if not yet present. When defined, the
//                     prompt renders as a compact trace.
//   payloadRenderer:  fn(payload, kind) → JSX. Host supplies the
//                     kind-specific renderer. Unknown kinds (including
//                     a missing renderer) fall back to
//                     genericPayloadRenderer.
//   onResolved:       fn(resolvedBody) → void. Called after a
//                     successful POST so the host can update higher-up
//                     state. Optional.
//   displayName:      fn(principalURN) → string. Optional. Defaults to
//                     a "last URN segment" rendering so the prompt
//                     works in isolation (tests, the Overview, etc.).
//   now:              () => number. Test seam for the countdown timer.
//                     Defaults to Date.now.
export function ApprovalPrompt({
  request,
  resolved,
  payloadRenderer,
  onResolved,
  displayName,
  now,
}) {
  const body = request?.body || request?.payload || {};
  const requestID = body.request_id || '';
  const kind = String(body.kind || '');
  const summary = body.summary || '';
  const requestor = request?.actor_id || '';
  const expiresAt = body.expires_at || '';

  // Local state per request_id. Source of truth is the
  // `approval.resolved` event arriving on the thread; this is for
  // snappiness so the buttons disable immediately on click without
  // waiting for the round-trip. When `resolved` is provided, it wins
  // (reconciles local optimistic state against the operator-minted
  // event).
  const [localState, setLocalState] = useState('pending');
  const [reason, setReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Tick the countdown once per second. The interval is cleaned up on
  // unmount and is a no-op when the prompt is already in a terminal
  // state (resolved or expired) to avoid pointless renders.
  //
  // `nowFn` is memoised on the `now` prop identity. Without this the
  // default-branch `() => Date.now()` is a fresh closure every render,
  // which would tear down and recreate the setInterval every tick (the
  // effect updates state every second, which re-runs render). The memo
  // keeps the effect's dep array stable so the interval is created once
  // per mount.
  const nowFn = useMemo(() => now || (() => Date.now()), [now]);
  const [nowMs, setNowMs] = useState(() => nowFn());
  const resolvedBody = resolved?.body || resolved?.payload || null;
  const terminalState = decisionFromResolved(resolvedBody) || (localState !== 'pending' ? localState : null);
  useEffect(() => {
    if (terminalState) return undefined;
    const handle = setInterval(() => setNowMs(nowFn()), 1000);
    return () => clearInterval(handle);
  }, [terminalState, nowFn]);

  const expiresMs = useMemo(() => {
    if (!expiresAt) return null;
    const t = Date.parse(expiresAt);
    if (Number.isNaN(t)) return null;
    return t;
  }, [expiresAt]);

  const remainingMs = expiresMs !== null ? expiresMs - nowMs : null;
  const optimisticallyExpired = remainingMs !== null && remainingMs <= 0;

  async function submit(decision) {
    if (!requestID) {
      setSubmitError('request_id missing on event');
      return;
    }
    if (decision === 'rejected' && !reason.trim()) {
      // Surface inline; don't fire the POST since the server would
      // reject with 400 anyway.
      setSubmitError('reason required when rejecting');
      setShowReasonInput(true);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    // Snappy local update — buttons disable immediately so a double-click
    // can't double-submit. The reconcile on the resolved event arriving
    // is a no-op when local state already matches.
    setLocalState(decision);
    try {
      // resolveApproval returns `{event, conflict}`. A conflict (409 from
      // the BFF, per JOBE-APPROVAL-events §6) carries the EXISTING
      // resolved event body and is treated as a successful reconcile:
      // the operator-authoritative resolution wins, and we hand it to
      // the host so the trace flips to whatever was already recorded.
      const { event: respBody, conflict } = await resolveApproval(requestID, decision, reason.trim());
      // On 409 the BFF returns the operator-authoritative existing body.
      // Correct local state so the UI reflects what was actually recorded.
      if (conflict && respBody?.decision) {
        setLocalState(respBody.decision);
      }
      if (typeof onResolved === 'function') {
        onResolved(respBody);
      }
    } catch (err) {
      const status = err && err.status;
      if (status === 410) {
        setLocalState('expired');
      } else {
        // Roll back so the user can retry.
        setLocalState('pending');
        const msg = (err && err.message) || 'submit failed';
        setSubmitError(`${status ? `(${status}) ` : ''}${msg}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Compact trace mode: the operator-minted resolved event has
  // arrived, or local optimistic state is terminal. Both render the
  // same shape so the trace looks identical whether the user resolved
  // here or another surface did.
  if (terminalState) {
    return renderTrace({
      requestID,
      kind,
      summary,
      requestor,
      resolvedBody,
      localTerminalState: terminalState,
      displayName,
    });
  }

  // renderPayload invokes the host-supplied renderer first; if it
  // returns null/undefined (the convention used in Chat.jsx, where a
  // dispatcher returns null for unknown kinds to opt into the generic
  // fallback), we fall back to genericPayloadRenderer. Per
  // JOBE-APPROVAL-events §2.1 unknown kinds MUST render the generic
  // JSON fallback, not an empty payload.
  const renderPayload = (pl, k) => {
    if (typeof payloadRenderer === 'function') {
      const out = payloadRenderer(pl, k);
      if (out !== null && out !== undefined) return out;
    }
    return genericPayloadRenderer(pl);
  };
  const requestorDisplay = displayName ? displayName(requestor) : shortenURN(requestor);
  const countdown = remainingMs !== null ? fmtCountdown(remainingMs) : '';

  return (
    <div
      className="ev-row approval-row approval-pending"
      data-testid="approval-prompt"
      data-request-id={requestID}
      role="region"
      aria-label="Approval request"
    >
      <div className="approval-header">
        <span className="approval-kind-badge mono" data-testid="approval-kind">
          {kind || 'approval'}
        </span>
        <span className="approval-summary" data-testid="approval-summary">
          {summary || '(no summary)'}
        </span>
        <span className="approval-requestor mono dim" title={requestor}>
          from {requestorDisplay}
        </span>
        {countdown && (
          <span
            className={`approval-countdown mono dim${optimisticallyExpired ? ' approval-countdown-expired' : ''}`}
            data-testid="approval-countdown"
            title={`expires_at ${expiresAt}`}
          >
            {countdown}
          </span>
        )}
      </div>

      <div className="approval-payload">
        {renderPayload(body.payload, kind)}
      </div>

      {showReasonInput && (
        <div className="approval-reason-row">
          <label className="approval-reason-label mono dim" htmlFor={`approval-reason-${requestID}`}>
            reason (required to reject)
          </label>
          <input
            id={`approval-reason-${requestID}`}
            data-testid="approval-reason-input"
            className="approval-reason-input"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>
      )}

      {submitError && (
        <div className="approval-error mono" role="alert" data-testid="approval-error">
          {submitError}
        </div>
      )}

      <div className="approval-actions">
        <button
          type="button"
          className="approval-btn approval-btn-approve"
          data-testid="approval-btn-approve"
          disabled={submitting || optimisticallyExpired}
          onClick={() => submit('approved')}
        >
          Approve
        </button>
        <button
          type="button"
          className="approval-btn approval-btn-reject"
          data-testid="approval-btn-reject"
          disabled={submitting || optimisticallyExpired}
          onClick={() => {
            if (!showReasonInput) {
              setShowReasonInput(true);
              return;
            }
            submit('rejected');
          }}
        >
          {showReasonInput ? 'Confirm reject' : 'Reject'}
        </button>
      </div>
    </div>
  );
}

// renderTrace renders the post-resolution compact row. The shape is
// stable across `approved | rejected | expired` so chat-row layout
// doesn't shift after resolution. Field choice mirrors
// JOBE-APPROVAL-events §2.2's body: resolver_principal (omitted on
// expiry), reason (required on rejection), resolved_at.
function renderTrace({ requestID, kind, summary, requestor, resolvedBody, localTerminalState, displayName }) {
  const decision = resolvedBody?.decision || localTerminalState || 'pending';
  const expired = resolvedBody?.expired === true || decision === 'expired';
  const resolver = resolvedBody?.resolver_principal || '';
  const resolverDisplay = displayName && resolver ? displayName(resolver) : shortenURN(resolver);
  const reason = resolvedBody?.reason || '';
  const resolvedAt = resolvedBody?.resolved_at || '';
  const status = expired ? 'expired' : decision;

  return (
    <div
      className={`ev-row approval-row approval-trace approval-${status}`}
      data-testid="approval-trace"
      data-request-id={requestID}
      role="region"
      aria-label="Approval resolved"
    >
      <span className="approval-trace-status mono" data-testid="approval-trace-status">
        {status}
      </span>
      <span className="approval-kind-badge mono dim">{kind || 'approval'}</span>
      <span className="approval-trace-summary dim">{summary}</span>
      {!expired && resolver && (
        <span className="approval-trace-resolver mono dim" title={resolver}>
          by {resolverDisplay}
        </span>
      )}
      {expired && (
        <span className="approval-trace-resolver mono dim">
          by expiry sweep
        </span>
      )}
      {resolvedAt && (
        <span className="approval-trace-ts mono dim" title={resolvedAt}>
          {fmtTime(resolvedAt)}
        </span>
      )}
      {reason && (
        <span className="approval-trace-reason mono dim">— {reason}</span>
      )}
      {/* requestor anchor stays visible so the trace remains queryable
          in long transcripts. */}
      {requestor && (
        <span className="approval-trace-requestor mono dim" title={requestor}>
          for {shortenURN(requestor)}
        </span>
      )}
    </div>
  );
}

// shortenURN returns the last colon-separated segment of an URN — a
// reasonable default display when no host-supplied `displayName` is
// available. Per JOBE-AUTH-urn-egress-retire the wire form is the
// full URN; this is purely a display affordance and tooltips preserve
// the canonical value.
function shortenURN(urn) {
  if (!urn) return '';
  const parts = String(urn).split(':');
  return parts[parts.length - 1] || urn;
}

// chatToolInvokePayloadRenderer is the renderer the chat surface
// supplies for `kind: chat.tool_invoke` approvals. Lives here (not in
// Chat.jsx) so other hosts can import the same renderer when they
// embed chat-style tool-invoke approvals. Per JOBE-APPROVAL-events
// §2.1 the payload shape is `{tool_urn, args, target_resource?}`.
export function chatToolInvokePayloadRenderer(payload) {
  const p = payload || {};
  const toolURN = String(p.tool_urn || '');
  const target = p.target_resource ? String(p.target_resource) : '';
  let prettyArgs = '';
  try {
    prettyArgs = JSON.stringify(p.args ?? null, null, 2);
  } catch {
    prettyArgs = String(p.args ?? '');
  }
  return (
    <div className="approval-payload-tool-invoke">
      <div className="approval-payload-row">
        <span className="approval-payload-label mono dim">tool</span>
        <span className="approval-payload-value mono" data-testid="approval-tool-urn">
          {toolURN}
        </span>
      </div>
      {target && (
        <div className="approval-payload-row">
          <span className="approval-payload-label mono dim">target</span>
          <span className="approval-payload-value mono">{target}</span>
        </div>
      )}
      <div className="approval-payload-row">
        <span className="approval-payload-label mono dim">args</span>
        <pre className="approval-payload-value mono">{prettyArgs}</pre>
      </div>
    </div>
  );
}
